/**
 * "Launch demo": recorrido interactivo por Finance 360 (/tour). Cada paso muestra la
 * grabación real de esa pantalla (empresa ficticia Café Altura S.A.) y qué notar en ella.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { SelectorIdioma, useMeta } from './Marco';
import { useT } from './idioma';
import { Ventana } from './Inicio';
import './sitio.css';

export default function Recorrido() {
  const T = useT();
  const t = T.tour;
  useMeta(t.meta);
  const total = t.pasos.length;
  const [i, setI] = useState(0);
  const [vistos, setVistos] = useState(() => new Set([0]));
  const [fin, setFin] = useState(false);

  const ir = (n) => { setFin(false); setI(n); setVistos(v => new Set(v).add(n)); };
  const siguiente = () => (i + 1 < total ? ir(i + 1) : setFin(true));
  const paso = t.pasos[i];

  return (
    <div className="f360-sitio">
    <div className="noche" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="regla" />
      <header className="caja" style={{ maxWidth: 1440, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', paddingTop: 18, paddingBottom: 18, borderBottom: '1px solid rgba(255,255,255,.1)' }}>
        <Link to="/" aria-label="Finance 360"><img src="/marca/f360-logo-blanco-2026.svg" alt="Finance360 by Kuanta Bridge" style={{ height: 34, display: 'block' }} /></Link>
        <span className="mono" style={{ fontSize: 12.5, color: '#AEB4DD', flex: '1 1 auto' }}>{t.etiqueta}</span>
        <div className="nav oscura" style={{ padding: 0, gap: 14 }}><SelectorIdioma /></div>
        <Link to="/demo" className="pildora primaria" style={{ minHeight: 42, fontSize: 14.5 }}>{T.nav.demo}</Link>
        <Link to="/" className="redondo" aria-label={t.salir} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></Link>
      </header>

      <main className="caja" style={{ maxWidth: 1440, flex: '1 0 auto', display: 'flex', flexWrap: 'wrap', gap: 32, paddingTop: 32, paddingBottom: 40, alignItems: 'flex-start' }}>
        <div style={{ flex: '999 1 640px', minWidth: 0 }}>
          {fin ? (
            <div className="entra" style={{ borderRadius: 18, border: '1px solid rgba(255,255,255,.14)', padding: '72px 40px', textAlign: 'center' }}>
              <h1 className="display" style={{ fontSize: 'clamp(34px,4vw,56px)' }}>{t.finTitulo}</h1>
              <p className="suave" style={{ fontSize: 18, maxWidth: 560, margin: '18px auto 0' }}>{t.finTexto}</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
                <Link to="/demo" className="pildora blanca">{T.nav.demo}</Link>
                <Link to="/pricing" className="pildora borde">{t.verPrecios}</Link>
                <button type="button" className="pildora borde" onClick={() => ir(0)}>{t.otraVez}</button>
              </div>
            </div>
          ) : (
            <>
              <Ventana key={paso.clip} className="entra" clip={paso.clip} etiqueta={paso.titulo} url="cafe-altura.finance360.cr" />
              <p className="mono" style={{ fontSize: 12, color: '#AEB4DD', margin: '12px 0 0' }}>{t.nota}</p>
            </>
          )}
        </div>

        <aside aria-label={t.guia} style={{ flex: '1 1 340px', minWidth: 0, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 18, padding: 24 }}>
          <div className="mono" style={{ fontSize: 12, color: '#8E98F0', letterSpacing: '.06em', textTransform: 'uppercase' }}>{t.guia}</div>
          <ol style={{ listStyle: 'none', margin: '16px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {t.pasos.map((p, n) => {
              const activo = n === i && !fin;
              return (
                <li key={p.clip}>
                  <button type="button" onClick={() => ir(n)} aria-current={activo ? 'step' : undefined}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', minHeight: 44, padding: '8px 10px', borderRadius: 10, border: 0, cursor: 'pointer', font: 'inherit', fontSize: 15, background: activo ? 'rgba(142,152,240,.16)' : 'transparent', color: activo ? '#fff' : '#C9CEEA' }}>
                    <span aria-hidden="true" style={{ flex: '0 0 24px', height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', background: vistos.has(n) && !activo ? '#6FDBA8' : activo ? '#8E98F0' : 'rgba(255,255,255,.1)', color: vistos.has(n) || activo ? '#1A1D4E' : '#C9CEEA' }}>
                      {vistos.has(n) && !activo ? <Check size={14} /> : n + 1}
                    </span>
                    <span style={{ fontWeight: activo ? 600 : 400 }}>{p.titulo}</span>
                    {vistos.has(n) && !activo && <span className="solo-lector">{t.visto}</span>}
                  </button>
                </li>
              );
            })}
          </ol>

          {!fin && (
            <div className="entra" key={i} style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,.12)' }}>
              <div className="mono" style={{ fontSize: 12, color: '#AEB4DD' }}>{t.paso(i + 1, total)}</div>
              <h2 className="display" style={{ fontSize: 24, fontWeight: 500, margin: '8px 0 0' }}>{paso.titulo}</h2>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#8E98F0', margin: '16px 0 0' }}>{t.notar}</div>
              <ul className="puntos" style={{ marginTop: 10, gap: 10 }}>{paso.notar.map(x => <li key={x}><div><span style={{ color: '#E4E7F7' }}>{x}</span></div></li>)}</ul>
              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button type="button" className="pildora borde" disabled={i === 0} onClick={() => ir(i - 1)} style={{ minHeight: 44, opacity: i === 0 ? 0.4 : 1 }}><ArrowLeft size={16} />{t.anterior}</button>
                <button type="button" className="pildora primaria" onClick={siguiente} style={{ minHeight: 44, flex: 1 }}>{i + 1 < total ? t.siguiente : t.terminar}<ArrowRight size={16} /></button>
              </div>
            </div>
          )}
        </aside>
      </main>
    </div>
    </div>
  );
}
